import { enqueueSnackbar } from "notistack";
import { apiGet, apiPatch, apiPost } from "../../common/utils/request.utils";
import { AppDispatch } from "../../store";
import {
	getPricingError,
	resetPricings,
	setExportLoadingError,
	setExportLoadingState,
	getSitePricingsSuccess,
	transitionError,
	setLoader,
	setPricingRunLoader,
	pricingRunSuccess,
	PricingStatus,
	getPartiesSuccess,
	getPartiesError,
	setPricingTender,
	getCancelledPricingsError,
	Pricing,
	setPricings,
	getGraphTypesSuccess,
	getGraphError,
	setPricingTso,
	LoaderName,
	setPricingOptions,
} from "./pricingListSlice";
import {
	applyLinqPredicates,
	buildPredicates,
	setSelectedFilterValues,
} from "../filters/utils";
import { RootState } from "../../core/rootReducers";
import { selectPricingState } from "./pricingList.selector";
import {
	selectPersistedFilters,
	selectPricingFilters,
} from "../filters/filters.selector";
import * as liveUpdate from "../live_update/LiveUpdate.merge";
import * as _ from "lodash";
import { AxiosResponse } from "axios";
import {
	updatePricingTypeFilters,
	updateDeliveryPeriodsFilters,
	updatePartiesFilters,
} from "../filters/filters.thunk";
import * as odata from "../../common/utils/odata";

export function resetAllPricings() {
	return function (dispatch: AppDispatch) {
		dispatch(resetPricings());
	};
}

export function clearTransitionError() {
	return function (dispatch: AppDispatch) {
		dispatch(transitionError(undefined));
	};
}

export function getParties(search: string = ""): any {
	return function (dispatch: AppDispatch) {
		return apiPost(`external-services/parties`, { search: search }).then(
			(parties) => {
				return dispatch(getPartiesSuccess(parties.data?.partyResults));
			},
			(error) => {
				return dispatch(getPartiesError(error.message));
			}
		);
	};
}

export function downloadPricingRunsExport(filters: string, tenderId?: number) {
	return function (dispatch: AppDispatch) {
		dispatch(setExportLoadingState(true));
		const escapedFilters = filters?.length ? `"${filters}"` : "";
		let endpoint = `pricing-run/export/by-pricings?filters=${escapedFilters}`;
		if (tenderId) {
			endpoint += `&tender_id=${tenderId}`;
		}
		return apiGet(endpoint)
			.then((response) => {
				window.open(response.data.url);
			})
			.catch((err) => {
				enqueueSnackbar("Something went wrong", {
					variant: "error",
					autoHideDuration: 3000,
				});
				dispatch(setExportLoadingError(err));
			})
			.finally(() => dispatch(setExportLoadingState(false)));
	};
}

export function downloadPricingRunsExportByIdIn(
	ids: number[],
	tenderId?: number
) {
	return function (dispatch: AppDispatch) {
		dispatch(setExportLoadingState(true));
		const filters = ids?.length ? `"${odata.fieldIn("id", ids)}"` : "";
		let endpoint = `pricing-run/export?filters=${filters}`;
		if (tenderId) {
			endpoint += `&tender_id=${tenderId}`;
		}
		return apiGet(endpoint)
			.then((response) => {
				window.open(response.data.url);
			})
			.catch((err) => {
				enqueueSnackbar("Something went wrong", {
					variant: "error",
					autoHideDuration: 3000,
				});
				dispatch(setExportLoadingError(err));
			})
			.finally(() => dispatch(setExportLoadingState(false)));
	};
}

export function donwloadPricingPbInputs(pricingRunId: number) {
	return function (dispatch: AppDispatch) {
		dispatch(setExportLoadingState(true));
		return apiGet(`pricing-run/${pricingRunId}/pb-params`)
			.then((response) => {
				if (response?.data?.url) window.open(response.data.url);
			})
			.catch((err) => {
				enqueueSnackbar("Something went wrong", {
					variant: "error",
					autoHideDuration: 3000,
				});
				dispatch(setExportLoadingError(err));
			})
			.finally(() => dispatch(setExportLoadingState(false)));
	};
}

export function downloadPricingsExport(
	tender_id: number,
	statusIn: PricingStatus[] = [],
	format: string = "",
	forPricingsFilters: string | undefined = undefined
) {
	return function (dispatch: AppDispatch, getState: any) {
		dispatch(setExportLoadingState(true));
		const state = getState();
		const statusQueryParams = statusIn
			.map((status) => `status_in=${status}`)
			.join("&");
		const queries = [];
		if (state.filters.persistedFilters.pricings_query?.length) {
			queries.push(
				JSON.parse(state.filters.persistedFilters.pricings_query)
			);
		}

		if (forPricingsFilters?.length) {
			queries.push(forPricingsFilters);
		}

		const filters = JSON.stringify(queries.join(" and "));
		let endpoint = tender_id
			? `pricing/export/${tender_id}`
			: "pricing/export";
		endpoint += `?format=${format}&filters=${filters}${
			statusQueryParams ? "&" + statusQueryParams : ""
		}`;
		return apiGet(endpoint)
			.then((response) => {
				if (response.data.msg) {
					enqueueSnackbar(response.data.msg, {
						variant: "warning",
						autoHideDuration: 5000,
					});
				} else {
					window.open(response.data.url);
				}
			})
			.catch((err) => {
				enqueueSnackbar("Something went wrong", {
					variant: "error",
					autoHideDuration: 3000,
				});
				dispatch(setExportLoadingError(err));
			})
			.finally(() => dispatch(setExportLoadingState(false)));
	};
}

export function submitTransition(endpoint: string, data: any) {
	return function (dispatch: AppDispatch) {
		dispatch(clearTransitionError());
		dispatch(setLoader({ name: "transition", value: true }));
		return apiPatch(endpoint, data)
			.then(() => {
				dispatch(setLoader({ name: "transition", value: false }));
			})
			.catch((err) => {
				enqueueSnackbar("Something went wrong", {
					variant: "error",
					autoHideDuration: 3000,
				});
				dispatch(transitionError(err?.response?.data));
			});
	};
}

export function loadPricingRuns(pricingId: number, page: number) {
	return async function (dispatch: AppDispatch) {
		dispatch(setPricingRunLoader({ pricingId, isLoading: true }));
		try {
			const response = await apiGet(
				`pricing-run?pricing_id=${pricingId}&page=${page}&per_page=5`
			);
			dispatch(
				pricingRunSuccess({ pricingId, runs: response.data.data })
			);
		} catch (error: any) {
			dispatch(getPricingError(error.message));
		} finally {
			dispatch(setPricingRunLoader({ pricingId, isLoading: false }));
		}
	};
}

export function getTender(id: number): any {
	return function (dispatch: AppDispatch) {
		const tenderUrl = `tenders/${id}`;
		dispatch(setLoader({ name: "tender", value: true }));
		return apiGet(tenderUrl)
			.then((response) => {
				return dispatch(setPricingTender(response.data));
			})
			.finally(() => {
				dispatch(setLoader({ name: "tender", value: false }));
			});
	};
}

export function mergeincomingPricings(
	tenderId: number,
	incomingPricings: Pricing[]
) {
	return function (dispatch: AppDispatch, getState: () => RootState) {
		const pricings = selectPricingState(getState()).pricings;

		const persistedFilters = selectPersistedFilters(getState());
		const pricingsFiltersData = selectPricingFilters(getState());

		const values = setSelectedFilterValues(
			pricingsFiltersData,
			persistedFilters.filters?.pricings_filters ?? ""
		);
		const predicates = buildPredicates(
			pricingsFiltersData,
			values,
			"Pricings"
		);

		const filteredPricings = liveUpdate.mergeIncomingPricings(
			pricings,
			incomingPricings.filter(
				(pricing) => pricing.tender_id === tenderId
			),
			[],
			predicates.linqPredicates
		);

		if (!_.isEqual(filteredPricings, pricings)) {
			dispatch(setPricings(filteredPricings));
		}
		// TODO, seulement si UNDER_PRICING ?
		dispatch(updatePricingTypeFilters(incomingPricings));
		dispatch(updateDeliveryPeriodsFilters(incomingPricings));
		dispatch(updatePartiesFilters(incomingPricings));
	};
}

// TODO Delete, function is unused.
export function mergeincomingUpdatedPricings(
	tenderId: number,
	incomingPricings: Pricing[]
) {
	return function (dispatch: AppDispatch, getState: () => RootState) {
		const persistedFilters = selectPersistedFilters(getState());
		const pricingsFiltersData = selectPricingFilters(getState());

		const values = setSelectedFilterValues(
			pricingsFiltersData,
			persistedFilters.filters?.pricings_filters ?? ""
		);
		const predicates = buildPredicates(
			pricingsFiltersData,
			values,
			"Pricings"
		);

		const pricings = selectPricingState(getState()).pricings;
		const pricingIds = new Set(pricings.map((pricing) => pricing.id));
		const pricingInterSection = incomingPricings.filter((pricing) =>
			pricingIds.has(pricing.id)
		);

		const pricingNotInInterSection = incomingPricings.filter(
			(pricing) => !pricingIds.has(pricing.id)
		);

		// Maybe there are updated pricings that before were not matching filters. Add them
		pricingNotInInterSection.forEach((p) => {
			const pricingCompliesWithFilters = applyLinqPredicates(
				[p],
				predicates.linqPredicates
			);
			if (pricingCompliesWithFilters.length > 0) {
				pricingInterSection.push(pricingCompliesWithFilters[0]);
			}
		});

		if (pricingInterSection.length) {
			dispatch(mergeincomingPricings(tenderId, pricingInterSection));
		}
	};
}

export function getCancelledPricings(url: string, tenderId?: string): any {
	return function (dispatch: AppDispatch) {
		return apiGet(url).then(
			(p) => {
				const pricings = p?.data?.data
					.map((item: Pricing) => {
						item.updated_at = item.updated_at + "Z";
						item.isNewUpdate = true;
						return item;
					})
					.filter(
						(pric: Pricing) => pric.tender_id === Number(tenderId)
					);
				return dispatch(
					mergeincomingPricings(Number(tenderId) || 0, pricings)
				);
			},
			(error) => {
				return dispatch(getCancelledPricingsError(error.message));
			}
		);
	};
}

export function getGraphTypes(group_id: number): any {
	return async function (dispatch: AppDispatch) {
		try {
			const response = await apiGet(`timeseries/types/${group_id}`);
			dispatch(getGraphTypesSuccess(response.data.timeseries));
		} catch (error: any) {
			dispatch(getGraphError(error.message));
		}
	};
}

export function getGraphData(id: number, kinds: string): any {
	return async function () {
		const listKinds: string[] = kinds.split(",");
		let installedCapa: number = 0;
		let horizon: { start_date: string; end_date: string } | undefined =
			undefined;
		let result: { [key: string]: any } = {};
		let l: Promise<AxiosResponse>[] = [];

		listKinds.forEach((t: string) => {
			l.push(apiGet(`timeseries/${id}?kinds=${t}`));
		});
		let values = await Promise.all(l);
		values.forEach((response: AxiosResponse) => {
			listKinds.forEach((t: string) => {
				if (response.data[t] && response.data[t].length > 0) {
					result[t] = response.data[t];
					installedCapa = response.data?.installed_capacity as number;
					horizon = response.data?.horizon;
				}
				result.error = response.data.error;
			});
		});
		return { result, installedCapa, horizon };
	};
}

export function getDataByPricingId(
	urlParam: string,
	pricingId: number,
	loaderName: LoaderName
): any {
	return function (dispatch: AppDispatch) {
		const url = urlParam.replace("{pricing_id}", pricingId.toString());
		dispatch(setLoader({ name: loaderName, value: true }));
		return apiGet(url)
			.then((response) => {
				if (loaderName === "pricingTso") {
					return dispatch(setPricingTso(response.data));
				}
				if (loaderName === "pricingOptions") {
					return dispatch(setPricingOptions(response.data));
				}
			})
			.finally(() => {
				dispatch(setLoader({ name: loaderName, value: false }));
			});
	};
}

export function getSitePricingsByPricingId(pricingId: number): any {
	return function (dispatch: AppDispatch) {
		let url = `pricing/${pricingId}/site-pricing`;
		return apiGet(url).then((response) =>
			dispatch(getSitePricingsSuccess(response.data))
		);
	};
}
